import React, { useState, useEffect, useCallback } from 'react';
import { isNativePlatform } from '../../../utils/platform';
import {
  type PrintDestination,
  type SavedBlePrinter,
  type BleDevice,
  getSelectedDestination,
  setSelectedDestination,
  getDestinationLabel,
  getSavedBlePrinter,
  saveBlePrinter,
  clearSavedBlePrinter,
  scanForPrinters,
  connectAndDiscover,
  requestBlePermissions,
  printRawByDestination,
} from '../../../utils/printer';

const PrinterConfigPage: React.FC = () => {
  const [destination, setDestination] = useState<PrintDestination>(getSelectedDestination);
  const [savedBle, setSavedBle] = useState<SavedBlePrinter | null>(getSavedBlePrinter);
  const [scanning, setScanning] = useState(false);
  const [foundDevices, setFoundDevices] = useState<BleDevice[]>([]);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [testPrinting, setTestPrinting] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'ok' | 'error' } | null>(null);

  const showMsg = useCallback((text: string, type: 'ok' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 8000);
  }, []);

  useEffect(() => {
    if (!isNativePlatform()) {
      showMsg('Esta pagina solo esta disponible en la APK.', 'error');
    }
  }, [showMsg]);

  const handleDestinationChange = (dest: PrintDestination) => {
    setDestination(dest);
    setSelectedDestination(dest);
    showMsg(`Impresora cambiada a: ${getDestinationLabel(dest)}`, 'ok');
  };

  const handleScan = async () => {
    setScanning(true);
    setFoundDevices([]);
    setMessage(null);
    try {
      await requestBlePermissions();
      const devices = await scanForPrinters(8000);
      setFoundDevices(devices);
      if (devices.length === 0) {
        showMsg(
          'No se encontraron impresoras BLE cercanas. Si tu impresora no aparece, puede que use Bluetooth Clasico (SPP) y no sea compatible con este modo.',
          'error',
        );
      } else {
        showMsg(`${devices.length} dispositivo(s) encontrado(s).`, 'ok');
      }
    } catch (err: any) {
      showMsg(`Error al escanear: ${err.message || err}`, 'error');
    } finally {
      setScanning(false);
    }
  };

  const handleConnect = async (device: BleDevice) => {
    setConnecting(device.deviceId);
    setMessage(null);
    try {
      const printer = await connectAndDiscover(device.deviceId);
      saveBlePrinter(printer);
      setSavedBle(printer);
      setFoundDevices([]);
      showMsg(`Conectada: ${printer.name}`, 'ok');
    } catch (err: any) {
      showMsg(`Error al conectar: ${err.message || err}`, 'error');
    } finally {
      setConnecting(null);
    }
  };

  const handleForget = () => {
    clearSavedBlePrinter();
    setSavedBle(null);
    showMsg('Impresora Bluetooth olvidada.', 'ok');
  };

  const handleTestPrint = async () => {
    setTestPrinting(true);
    setMessage(null);
    try {
      const result = await printRawByDestination(
        'TIEMPOS BARAHONA\n================================\nPrueba de impresion\nImpresora: ' +
          getDestinationLabel(destination) +
          '\nFecha: ' + new Date().toLocaleString('es-CR') +
          '\n================================\n\n\n',
        'TEST PRINT',
      );
      if (result.ok) {
        showMsg('Impresion de prueba enviada correctamente.', 'ok');
      } else {
        showMsg(`Error: ${result.error}`, 'error');
      }
    } catch (err: any) {
      showMsg(`Error: ${err.message || err}`, 'error');
    } finally {
      setTestPrinting(false);
    }
  };

  if (!isNativePlatform()) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Configuracion de Impresora</h1>
        <p className="text-gray-500">Esta configuracion solo esta disponible en la APK.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-2xl font-bold mb-6">Configuracion de Impresora</h1>

      <div className="max-w-lg space-y-6">
        {/* --- Seleccion de destino --- */}
        <div className="bg-white rounded-lg shadow p-5 space-y-3">
          <h2 className="text-lg font-semibold border-b pb-2">Metodo de impresion</h2>

          <label className="flex items-center gap-3 p-3 rounded-md border cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="dest"
              checked={destination === 'sunmi'}
              onChange={() => handleDestinationChange('sunmi')}
              className="w-4 h-4 text-blue-600"
            />
            <div>
              <div className="font-medium">SUNMI Integrada</div>
              <div className="text-sm text-gray-500">Impresora termica interna del dispositivo SUNMI</div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 rounded-md border cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="dest"
              checked={destination === 'bluetooth-ble'}
              onChange={() => handleDestinationChange('bluetooth-ble')}
              className="w-4 h-4 text-blue-600"
            />
            <div>
              <div className="font-medium">Bluetooth Externa</div>
              <div className="text-sm text-gray-500">Impresora termica Bluetooth externa</div>
            </div>
          </label>
        </div>

        {/* --- Seccion Bluetooth --- */}
        {destination === 'bluetooth-ble' && (
          <div className="bg-white rounded-lg shadow p-5 space-y-4">
            <h2 className="text-lg font-semibold border-b pb-2">Impresora Bluetooth</h2>

            {savedBle ? (
              <div className="p-3 bg-green-50 rounded-md border border-green-200 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-green-800">{savedBle.name}</div>
                  <button
                    onClick={handleForget}
                    className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                  >
                    Olvidar
                  </button>
                </div>
                <div className="text-xs text-green-600">{savedBle.deviceId}</div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No hay impresora Bluetooth guardada.</p>
            )}

            <button
              onClick={handleScan}
              disabled={scanning}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:bg-gray-400"
            >
              {scanning ? 'Buscando...' : 'Buscar impresoras'}
            </button>

            {foundDevices.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  Dispositivos encontrados ({foundDevices.length}):
                </p>
                {foundDevices.map((dev) => (
                  <div
                    key={dev.deviceId}
                    className="flex items-center justify-between p-3 border rounded-md"
                  >
                    <div>
                      <div className="font-medium">{dev.name || 'Sin nombre'}</div>
                      <div className="text-xs text-gray-500">{dev.deviceId}</div>
                    </div>
                    <button
                      onClick={() => handleConnect(dev)}
                      disabled={connecting === dev.deviceId}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                    >
                      {connecting === dev.deviceId ? 'Conectando...' : 'Conectar'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* --- Prueba de impresion --- */}
        <div className="bg-white rounded-lg shadow p-5 space-y-3">
          <h2 className="text-lg font-semibold border-b pb-2">Prueba de impresion</h2>
          <p className="text-sm text-gray-500">
            Destino actual: <strong>{getDestinationLabel(destination)}</strong>
          </p>
          <button
            onClick={handleTestPrint}
            disabled={testPrinting}
            className="w-full py-2 px-4 bg-gray-800 text-white rounded-md font-medium hover:bg-gray-900 disabled:bg-gray-400"
          >
            {testPrinting ? 'Imprimiendo...' : 'Imprimir prueba'}
          </button>
        </div>

        {/* --- Mensajes --- */}
        {message && (
          <div
            className={`p-3 rounded-md text-sm ${
              message.type === 'ok'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrinterConfigPage;
